import type { Translate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";

import { ResponseError } from "app/services/fetchService";

export const GSC_ERROR_BASE = "https://api-docs.similarweb.com/error/";

export const GSC_ERROR_TYPES = {
    RECONNECT_REQUIRED: `${GSC_ERROR_BASE}google-search-console-reconnect-required`,
    ACCESS_DENIED: `${GSC_ERROR_BASE}google-search-console-access-denied`,
    PROPERTY_UNAVAILABLE: `${GSC_ERROR_BASE}google-search-console-property-unavailable`,
    QUOTA_EXHAUSTED: `${GSC_ERROR_BASE}quota-exhausted`,
    RATE_LIMIT: `${GSC_ERROR_BASE}rate-limit`,
    GATEWAY_ERROR: `${GSC_ERROR_BASE}gateway-error`,
    SERVICE_UNAVAILABLE: `${GSC_ERROR_BASE}service-unavailable`,
    GATEWAY_TIMEOUT: `${GSC_ERROR_BASE}gateway-timeout`,
} as const;

export const GSC_COMMON_API_ERROR_KEY = "common.api.error";

const GSC_I18N_KEYS = {
    RECONNECT: "rankTracker.gsc.accountPicker.reconnectError",
    ACCESS_DENIED: "rankTracker.gsc.accountPicker.accessDeniedError",
    PROPERTY_UNAVAILABLE: "rankTracker.gsc.accountPicker.propertyUnavailableError",
    QUOTA_OR_RATE_LIMIT: "rankTracker.gsc.accountPicker.quotaOrRateLimitError",
    GENERAL: "rankTracker.gsc.accountPicker.generalError",
} as const;

const problemTypeToI18nKeyMap: Record<string, string> = {
    [GSC_ERROR_TYPES.RECONNECT_REQUIRED]: GSC_I18N_KEYS.RECONNECT,
    [GSC_ERROR_TYPES.ACCESS_DENIED]: GSC_I18N_KEYS.ACCESS_DENIED,
    [GSC_ERROR_TYPES.PROPERTY_UNAVAILABLE]: GSC_I18N_KEYS.PROPERTY_UNAVAILABLE,
    [GSC_ERROR_TYPES.QUOTA_EXHAUSTED]: GSC_I18N_KEYS.QUOTA_OR_RATE_LIMIT,
    [GSC_ERROR_TYPES.RATE_LIMIT]: GSC_I18N_KEYS.QUOTA_OR_RATE_LIMIT,
    [GSC_ERROR_TYPES.GATEWAY_ERROR]: GSC_I18N_KEYS.GENERAL,
    [GSC_ERROR_TYPES.SERVICE_UNAVAILABLE]: GSC_I18N_KEYS.GENERAL,
    [GSC_ERROR_TYPES.GATEWAY_TIMEOUT]: GSC_I18N_KEYS.GENERAL,
};

export const getGscProblemType = (error: unknown): string | undefined => {
    return error instanceof ResponseError ? error.problem?.type : undefined;
};

export const isGscReconnectRequiredError = (error: unknown): boolean => {
    return getGscProblemType(error) === GSC_ERROR_TYPES.RECONNECT_REQUIRED;
};

/** True when problem.type has dedicated UI copy (mapped in problemTypeToI18nKeyMap). */
export const isGscHandledProblemError = (error: unknown): boolean => {
    const problemType = getGscProblemType(error);
    return problemType != null && problemType in problemTypeToI18nKeyMap;
};

export const getGscErrorI18nKey = (error: unknown): string => {
    const problemType = getGscProblemType(error);
    if (!problemType) {
        return GSC_COMMON_API_ERROR_KEY;
    }

    return problemTypeToI18nKeyMap[problemType] ?? GSC_COMMON_API_ERROR_KEY;
};

export const getGscErrorMessage = (error: unknown, translate: Translate): string => {
    const key = getGscErrorI18nKey(error);

    if (key === GSC_COMMON_API_ERROR_KEY) {
        const detail = error instanceof ResponseError ? error.problem?.detail : undefined;
        if (detail) {
            return translate(key, { error: detail });
        }
        return translate(key);
    }

    return translate(key);
};

export const getGscSentryExtras = (error: unknown): { problemType: string | undefined } => ({
    problemType: getGscProblemType(error),
});

/** React Query throwOnError that reports only unhandled GSC failures to Sentry. */
export const handleGscThrowOnError =
    (message: string) =>
    (error: unknown): boolean => {
        if (!isGscHandledProblemError(error)) {
            Sentry.errorTracker({
                e: error,
                level: "error",
                message,
                params: { extra: getGscSentryExtras(error) },
            });
        }
        return false;
    };
